import CompanyAPI from 'dashboard/api/companies';
import { createStore } from 'dashboard/store/storeFactory';
import { throwErrorMessage } from 'dashboard/store/utils/api';
import camelcaseKeys from 'camelcase-keys';

export const useCompaniesStore = createStore({
  name: 'companies',
  type: 'pinia',
  API: CompanyAPI,
  getters: {
    getCompaniesList: state => {
      return state.records;
    },
    getCompanyById: state => id => {
      return state.records.find(r => r.id === Number(id)) || null;
    },
  },
  actions: () => ({
    async get(params = {}) {
      this.setUIFlag({ fetchingList: true });
      try {
        const { data } = await CompanyAPI.get(params);
        this.records = camelcaseKeys(data.payload || data, { deep: true });
        if (data.meta) this.setMeta(data.meta);
        return this.records;
      } catch (error) {
        throwErrorMessage(error);
        return [];
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async search({ search, page, sort }) {
      this.setUIFlag({ fetchingList: true });
      try {
        const {
          data: { payload, meta },
        } = await CompanyAPI.search(search, page, sort);
        this.records = camelcaseKeys(payload, { deep: true });
        this.setMeta(meta);
      } catch (error) {
        throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async fetchCompany(companyId) {
      this.setUIFlag({ fetching: true });
      try {
        const {
          data: { payload },
        } = await CompanyAPI.show(companyId);
        const converted = camelcaseKeys(payload, { deep: true });
        const idx = this.records.findIndex(r => r.id === converted.id);
        if (idx >= 0) {
          this.records[idx] = converted;
        } else {
          this.records.push(converted);
        }
        return converted;
      } catch (error) {
        throwErrorMessage(error);
        return null;
      } finally {
        this.setUIFlag({ fetching: false });
      }
    },

    async createCompany(data) {
      this.setUIFlag({ creating: true });
      try {
        const {
          data: { payload },
        } = await CompanyAPI.create(data);
        const converted = camelcaseKeys(payload, { deep: true });
        this.records.push(converted);
        return converted;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ creating: false });
      }
    },

    async updateCompany(companyId, data) {
      this.setUIFlag({ updating: true });
      try {
        const {
          data: { payload },
        } = await CompanyAPI.update(companyId, data);
        const converted = camelcaseKeys(payload, { deep: true });
        const idx = this.records.findIndex(r => r.id === converted.id);
        if (idx >= 0) {
          this.records[idx] = converted;
        }
        return converted;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ updating: false });
      }
    },

    async deleteCompany(companyId) {
      this.setUIFlag({ deleting: true });
      try {
        await CompanyAPI.delete(companyId);
        this.records = this.records.filter(r => r.id !== companyId);
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ deleting: false });
      }
    },

    async fetchContacts(companyId, page = 1) {
      this.setUIFlag({ fetchingContacts: true });
      try {
        const { data } = await CompanyAPI.getContacts(companyId, page);
        return data;
      } catch (error) {
        throwErrorMessage(error);
        return { payload: [], meta: { total_count: 0 } };
      } finally {
        this.setUIFlag({ fetchingContacts: false });
      }
    },

    async fetchConversations(companyId, page = 1) {
      this.setUIFlag({ fetchingConversations: true });
      try {
        const { data } = await CompanyAPI.getConversations(companyId, page);
        return data;
      } catch (error) {
        throwErrorMessage(error);
        return { payload: [], meta: { total_count: 0 } };
      } finally {
        this.setUIFlag({ fetchingConversations: false });
      }
    },

    async fetchDeals(companyId, page = 1) {
      this.setUIFlag({ fetchingDeals: true });
      try {
        const { data } = await CompanyAPI.getDeals(companyId, page);
        return data;
      } catch (error) {
        throwErrorMessage(error);
        return { payload: [], meta: { total_count: 0 } };
      } finally {
        this.setUIFlag({ fetchingDeals: false });
      }
    },
  }),
});
